import Axios from 'axios';
import qs from 'qs';

import { publicPaths } from './variables';

import type { AxiosError, AxiosRequestConfig } from 'axios';

import { AppRoutes } from '@/constants/routes';
import { sanitizeFilename } from '@/utils/file.utils';

const sanitizeFormDataFilenames = (data: unknown): unknown => {
  if (!(data instanceof FormData)) return data;

  const sanitized = new FormData();
  for (const [key, value] of data.entries()) {
    if (value instanceof File) {
      const safeFile = new File([value], sanitizeFilename(value.name), { type: value.type });
      sanitized.append(key, safeFile);
    } else {
      sanitized.append(key, value);
    }
  }
  return sanitized;
};

const createAxiosInstance = () => {
  const axios = Axios.create({
    baseURL: '/api',
    withCredentials: true,
    paramsSerializer: {
      serialize: params =>
        qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }),
    },
  });

  axios.interceptors.request.use((config) => {
    config.data = sanitizeFormDataFilenames(config.data);
    return config;
  });

  axios.interceptors.response.use(undefined, async(error) => {
    const status = error?.response?.status;

    if (status !== 401) {
      return Promise.reject(error);
    }

    if (!import.meta.client) {
      return Promise.reject(error);
    }

    const router = useRouter();
    const path = router.currentRoute.value.path;

    const isPublic = publicPaths.some(publicPath => path.startsWith(publicPath));

    if (isPublic) {
      return Promise.reject(error);
    }

    console.warn('Unauthorized – redirecting to /login');
    const loginPath = withQueryParams(AppRoutes.AUTH_LOGIN, { redirect: path });
    try {
      await navigateTo(loginPath);
    } catch {
      if (import.meta.client) window.location.href = loginPath;
    }

    return Promise.reject(error);
  });

  return axios;
};

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const AXIOS_INSTANCE = createAxiosInstance();
  const source = Axios.CancelToken.source();

  const promise = AXIOS_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // @ts-expect-error - This is a custom instance, so we can add our own properties
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = AxiosError<Error>;

type CamelCaseString<S> =
  S extends `${infer F}_${infer R}`
    ? `${Lowercase<F>}${Capitalize<CamelCaseString<R>>}`
    : S;

export type CamelCase<T> = {
  [K in keyof T as CamelCaseString<K & string>]: T[K]
};
export type BodyType<BodyData> = BodyData;
export type CamelCasedBodyType<BodyData> = CamelCase<BodyData>;
